import React, { useState, useRef, useEffect } from 'react';
import './DotSplitGenerator2.css';

const DotSplitGenerator2 = () => {
  const canvasRef = useRef(null);
  const [config, setConfig] = useState({
    n: 18,
    seed: 12345,
    lines: 3,
    parts: 4,
    maxW: 15,
    zoom: 'fit',
    pointSize: 8,
    fontSize: 24
  });

  const [status, setStatus] = useState({ text: 'idle', type: 'warn' });
  const [puzzle, setPuzzle] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [log, setLog] = useState('');
  const [running, setRunning] = useState(false);

  const viewBoxRef = useRef({ x: 0, y: 0, w: 840, h: 840 });
  const selRef = useRef({ active: false, ctrlDrag: false, startCanvas: null, currentCanvas: null, dragIndex: -1 });

  // Constants
  const CANVAS_W = 840, CANVAS_H = 840, PAD = 32;
  const MIN_POINT_DIST = 22;
  const MAX_CANDIDATE_LINES = 16, COMBO_CAP = 6500, ATTEMPT_CAP = 500, WEIGHT_TRIES_PER_COMBO = 60;
  const EPS_PAR = 1e-9;

  const EXPORT_CONFIG = {
    EXPORT_SIZE: 640,
    EXPORT_BORDER_PX: 10,
    EXPORT_POINT_R_PX: 8,
    EXPORT_FONT_PX: 30,
    EXPORT_SOLUTION_LINE_PX: 3
  };

  // RNG & helpers
  function mulberry32(seed) {
    let t = seed >>> 0;
    return () => {
      t += 0x6D2B79F5;
      let r = Math.imul(t ^ t >>> 15, 1 | t);
      r ^= r + Math.imul(r ^ r >>> 7, 61 | r);
      return ((r ^ r >>> 14) >>> 0) / 4294967296;
    };
  }

  function randInt(rng, a, b) { return a + Math.floor(rng() * (b - a + 1)); }

  function shuffleInPlace(a, rng) {
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function nCr(n, r) {
    if (r < 0 || r > n) return 0;
    r = Math.min(r, n - r);
    let num = 1, den = 1;
    for (let i = 1; i <= r; i++) {
      num *= (n - r + i);
      den *= i;
    }
    return Math.round(num / den);
  }

  // Geometry functions
  function normalizeLine(a, b, c) {
    const len = Math.hypot(a, b);
    if (len < 1e-12) throw new Error('degenerate');
    a /= len;
    b /= len;
    c /= len;
    if (a < -1e-12 || (Math.abs(a) <= 1e-12 && b < -1e-12)) {
      a = -a;
      b = -b;
      c = -c;
    }
    return { a, b, c };
  }

  function distPointLine(p, l) {
    return Math.abs(l.a * p.x + l.b * p.y + l.c);
  }

  function lineIntersect(l1, l2) {
    const D = l1.a * l2.b - l2.a * l1.b;
    if (Math.abs(D) < EPS_PAR) return null;
    const x = (l2.b * l1.c - l1.b * l2.c) / D;
    const y = (l1.a * l2.c - l2.a * l1.c) / D;
    return { x, y };
  }

  function countRegions(lines) {
    let f = 1;
    for (let i = 0; i < lines.length; i++) {
      const li = lines[i], pts = [];
      for (let j = 0; j < i; j++) {
        const p = lineIntersect(li, lines[j]);
        if (!p) continue;
        if (!pts.some(q => Math.hypot(p.x - q.x, p.y - q.y) < 1e-6)) pts.push(p);
      }
      f += pts.length + 1;
    }
    return f;
  }

  function regionCode(p, lines) {
    let s = '';
    for (const l of lines) {
      const v = l.a * p.x + l.b * p.y + l.c;
      if (Math.abs(v) < 1e-8) return null;
      s += (v > 0 ? '1' : '0');
    }
    return s;
  }

  function polygonArea(poly) {
    let a = 0;
    for (let i = 0; i < poly.length; i++) {
      const j = (i + 1) % poly.length;
      a += poly[i].x * poly[j].y - poly[j].x * poly[i].y;
    }
    return 0.5 * a;
  }

  function clipPolygonToHalfPlane(poly, l, keepPositive = true) {
    const out = [];
    if (!poly || poly.length === 0) return out;
    const EPS = 1e-9;

    function inside(pt) {
      const v = l.a * pt.x + l.b * pt.y + l.c;
      return keepPositive ? (v > EPS) : (v < -EPS);
    }

    function intersect(A, B) {
      const fA = l.a * A.x + l.b * A.y + l.c;
      const fB = l.a * B.x + l.b * B.y + l.c;
      const denom = fA - fB;
      if (Math.abs(denom) < 1e-12) return A;
      const t = fA / (fA - fB);
      return { x: A.x + t * (B.x - A.x), y: A.y + t * (B.y - A.y) };
    }

    for (let i = 0; i < poly.length; i++) {
      const A = poly[i], B = poly[(i + 1) % poly.length];
      const Ain = inside(A), Bin = inside(B);
      if (Ain && Bin) {
        out.push(B);
      } else if (Ain && !Bin) {
        out.push(intersect(A, B));
      } else if (!Ain && Bin) {
        out.push(intersect(A, B));
        out.push(B);
      }
    }
    return out;
  }

  function visibleRegionCodes(lines, W = CANVAS_W, H = CANVAS_H) {
    const rect = [{ x: 0, y: 0 }, { x: W, y: 0 }, { x: W, y: H }, { x: 0, y: H }];
    const L = lines.length;
    const codes = new Set();
    const MAX_MASK = 1 << L;

    for (let mask = 0; mask < MAX_MASK; mask++) {
      let poly = rect.slice();
      let ok = true;
      for (let i = 0; i < L; i++) {
        const keepPos = ((mask >> i) & 1) === 1;
        poly = clipPolygonToHalfPlane(poly, lines[i], keepPos);
        if (poly.length === 0) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      const area = Math.abs(polygonArea(poly));
      if (area > 1e-4) {
        let s = '';
        for (let i = 0; i < L; i++) {
          s += (((mask >> i) & 1) === 1) ? '1' : '0';
        }
        codes.add(s);
      }
    }
    return codes;
  }

  function clipLineToRect(l, W, H) {
    const pts = [];
    if (Math.abs(l.b) > EPS_PAR) {
      const y0 = -l.c / l.b;
      if (y0 >= -1 && y0 <= H + 1) pts.push({ x: 0, y: y0 });
      const yW = (-l.a * W - l.c) / l.b;
      if (yW >= -1 && yW <= H + 1) pts.push({ x: W, y: yW });
    }
    if (Math.abs(l.a) > EPS_PAR) {
      const x0 = -l.c / l.a;
      if (x0 >= -1 && x0 <= W + 1) pts.push({ x: x0, y: 0 });
      const xH = (-l.b * H - l.c) / l.a;
      if (xH >= -1 && xH <= W + 1) pts.push({ x: xH, y: H });
    }
    const uniq = [];
    for (const p of pts) {
      if (!uniq.some(q => Math.hypot(p.x - q.x, p.y - q.y) < 1e-6)) uniq.push(p);
      if (uniq.length === 2) break;
    }
    return (uniq.length < 2) ? null : uniq.slice(0, 2);
  }

  function perpBisector(p, q) {
    const mx = (p.x + q.x) / 2, my = (p.y + q.y) / 2;
    const vx = q.x - p.x, vy = q.y - p.y;
    if (Math.hypot(vx, vy) < 1e-8) return null;
    return normalizeLine(vx, vy, -(vx * mx + vy * my));
  }

  function canvasOffsetBounds(theta, W = CANVAS_W, H = CANVAS_H) {
    const a = Math.cos(theta), b = Math.sin(theta);
    const corners = [{ x: 0, y: 0 }, { x: W, y: 0 }, { x: 0, y: H }, { x: W, y: H }];
    let smin = Infinity, smax = -Infinity;
    for (const c of corners) {
      const s = a * c.x + b * c.y;
      if (s < smin) smin = s;
      if (s > smax) smax = s;
    }
    return { a, b, smin, smax };
  }

  function lineFromAngleOffset(theta, s0) {
    const a = Math.cos(theta), b = Math.sin(theta), c = -s0;
    return normalizeLine(a, b, c);
  }

  // Core generation
  function validateInputs(N, L, P) {
    if (!Number.isInteger(N) || !Number.isInteger(L) || !Number.isInteger(P))
      return { ok: false, msg: "All inputs must be integers." };
    if (N < 2) return { ok: false, msg: "N must be ≥ 2." };
    if (L < 1) return { ok: false, msg: "L must be ≥ 1." };
    if (P < 2) return { ok: false, msg: "P must be ≥ 2." };
    if (N < P) return { ok: false, msg: `N must be at least P so each part has ≥1 point (N=${N}, P=${P}).` };
    const Pmax = L * (L + 1) / 2 + 1;
    if (P > Pmax) return { ok: false, msg: `With L=${L}, max parts is ${Pmax}. Requested P=${P}.` };
    return { ok: true };
  }

  function generatePoints(N, rng) {
    const pts = [];
    let guard = 0;
    while (pts.length < N && guard < N * 500) {
      guard++;
      const x = PAD + rng() * (CANVAS_W - 2 * PAD);
      const y = PAD + rng() * (CANVAS_H - 2 * PAD);
      if (pts.every(p => Math.hypot(p.x - x, p.y - y) >= MIN_POINT_DIST))
        pts.push({ x, y });
    }
    return (pts.length === N) ? pts : null;
  }

  function buildCandidateLines(points, L, rng) {
    const seen = new Set();
    const lines = [];

    function pushUnique(l) {
      if (!l) return;
      for (const p of points) {
        if (distPointLine(p, l) < 0.7) return;
      }
      const key = `${l.a.toFixed(8)},${l.b.toFixed(8)},${l.c.toFixed(6)}`;
      if (seen.has(key)) return;
      seen.add(key);
      lines.push(l);
    }

    const pairs = [];
    for (let i = 0; i < points.length; i++)
      for (let j = i + 1; j < points.length; j++) pairs.push([i, j]);
    shuffleInPlace(pairs, rng);

    const MAX_BISECTORS = 40;
    for (let k = 0; k < pairs.length && k < MAX_BISECTORS; k++) {
      const [i, j] = pairs[k];
      pushUnique(perpBisector(points[i], points[j]));
    }

    const RANDOM_ANGLES = 10;
    const OFFSETS_PER_ANGLE = 4;
    for (let t = 0; t < RANDOM_ANGLES; t++) {
      const theta = rng() * Math.PI;
      const { a, b, smin, smax } = canvasOffsetBounds(theta);
      for (let k = 0; k < OFFSETS_PER_ANGLE; k++) {
        const s0 = smin + (k + 0.5) * (smax - smin) / OFFSETS_PER_ANGLE;
        pushUnique(lineFromAngleOffset(theta, s0));
        const jitter = (rng() - 0.5) * 0.02 * (smax - smin);
        pushUnique(lineFromAngleOffset(theta, s0 + jitter));
      }
    }

    const FAMILY_ANGLES = 6;
    const FAMILY_LINES_PER_ANGLE = 8;
    for (let q = 0; q < FAMILY_ANGLES; q++) {
      const theta = (q + 0.5) * Math.PI / FAMILY_ANGLES;
      const { a, b, smin, smax } = canvasOffsetBounds(theta);
      const proj = points.map(p => a * p.x + b * p.y).sort((x, y) => x - y);
      const between = [];
      for (let i = 0; i + 1 < proj.length; i++) {
        const mid = (proj[i] + proj[i + 1]) / 2;
        if (mid > smin && mid < smax) between.push(mid);
      }
      shuffleInPlace(between, rng);
      const take = Math.min(FAMILY_LINES_PER_ANGLE, between.length);
      for (let i = 0; i < take; i++) {
        pushUnique(lineFromAngleOffset(theta, between[i]));
      }
    }

    let M = lines.length;
    const MAX_TOTAL = Math.max(24, Math.min(80, MAX_CANDIDATE_LINES * 4));
    if (M > MAX_TOTAL) {
      shuffleInPlace(lines, rng);
      lines.length = MAX_TOTAL;
      M = lines.length;
    }

    while (M > L && nCr(M, L) > COMBO_CAP) {
      lines.pop();
      M = lines.length;
    }
    return (M >= L) ? lines : null;
  }

  function* combinations(m, k) {
    const idx = Array.from({ length: k }, (_, i) => i), last = m - 1;
    while (true) {
      yield idx.slice();
      let i = k - 1;
      while (i >= 0 && idx[i] === last - (k - 1 - i)) i--;
      if (i < 0) break;
      idx[i]++;
      for (let j = i + 1; j < k; j++) idx[j] = idx[j - 1] + 1;
    }
  }

  function regionsForPoints(points, lines) {
    const map = new Map();
    for (let i = 0; i < points.length; i++) {
      const code = regionCode(points[i], lines);
      if (!code) return null;
      if (!map.has(code)) map.set(code, []);
      map.get(code).push(i);
    }
    return { map, codes: [...map.keys()] };
  }

  function sampleWeightsForPartition(regions, N, S, rng, WMAX) {
    const weights = new Array(N).fill(0);
    for (const code of regions.codes) {
      const idxs = regions.map.get(code).slice();
      shuffleInPlace(idxs, rng);
      const k = idxs.length;
      if (k === 1) {
        const w = S;
        if (w < 1 || w > WMAX) return null;
        weights[idxs[0]] = w;
        continue;
      }
      let ok = false;
      for (let t = 0; t < 35 && !ok; t++) {
        let s = 0;
        for (let u = 0; u < k - 1; u++) {
          const w = randInt(rng, 1, WMAX);
          weights[idxs[u]] = w;
          s += w;
        }
        const last = S - s;
        if (last >= 1 && last <= WMAX) {
          weights[idxs[k - 1]] = last;
          ok = true;
        }
      }
      if (!ok) return null;
    }
    return weights;
  }

  function checkEqualSums(regions, weights) {
    let S = null;
    for (const code of regions.codes) {
      const idxs = regions.map.get(code);
      const sum = idxs.reduce((a, i) => a + weights[i], 0);
      if (S === null) S = sum;
      else if (sum !== S) return null;
    }
    return S;
  }

  function countSolutionsAcrossCombos(candidateLines, eligibleCombos, points, weights, P) {
    let hits = 0;
    for (const combo of eligibleCombos) {
      const lines = combo.map(i => candidateLines[i]);
      const part = regionsForPoints(points, lines);
      if (!part) continue;
      if (part.codes.length !== P) continue;
      const S = checkEqualSums(part, weights);
      if (S !== null) {
        hits++;
        if (hits > 1) break;
      }
    }
    return { hits };
  }

  function compactPointsInPlace(points, lines, opts = {}) {
    const iterations = opts.iterations ?? 10;
    const alpha = opts.alpha ?? 0.22;
    const lineMargin = opts.lineMargin ?? 6;
    const minDist = opts.minDist ?? 18;
    const boundsMargin = Math.max(lineMargin, config.pointSize + 4);
    const codes0 = points.map(p => regionCode(p, lines));
    const centroid = () => ({
      x: points.reduce((s, p) => s + p.x, 0) / points.length,
      y: points.reduce((s, p) => s + p.y, 0) / points.length
    });

    function limitMove(p, v) {
      let tmax = 1;
      for (const l of lines) {
        const f0 = l.a * p.x + l.b * p.y + l.c;
        const df = l.a * v.x + l.b * v.y;
        if ((f0 >= 0 && df < 0) || (f0 < 0 && df > 0)) {
          const t = (Math.abs(f0) - lineMargin) / Math.abs(df);
          if (t < tmax) tmax = Math.max(0, t);
        }
      }
      const left = PAD + boundsMargin, right = CANVAS_W - PAD - boundsMargin;
      const top = PAD + boundsMargin, bottom = CANVAS_H - PAD - boundsMargin;
      if (v.x < 0) tmax = Math.min(tmax, (p.x - left) / (-v.x));
      else if (v.x > 0) tmax = Math.min(tmax, (right - p.x) / v.x);
      if (v.y < 0) tmax = Math.min(tmax, (p.y - top) / (-v.y));
      else if (v.y > 0) tmax = Math.min(tmax, (bottom - p.y) / v.y);
      if (!Number.isFinite(tmax) || tmax < 0) tmax = 0;
      return { dx: v.x * tmax, dy: v.y * tmax };
    }

    for (let it = 0; it < iterations; it++) {
      const C = centroid();
      const proposed = points.map(p => {
        const v = { x: (C.x - p.x) * alpha, y: (C.y - p.y) * alpha };
        const lim = limitMove(p, v);
        return { x: p.x + lim.dx, y: p.y + lim.dy };
      });
      for (let i = 0; i < proposed.length; i++)
        for (let j = i + 1; j < proposed.length; j++) {
          let dx = proposed[i].x - proposed[j].x, dy = proposed[i].y - proposed[j].y, d = Math.hypot(dx, dy);
          if (d < minDist && d > 1e-6) {
            const overlap = (minDist - d) / 2;
            const ux = dx / d, uy = dy / d;
            const di = limitMove(proposed[i], { x: ux * overlap, y: uy * overlap });
            proposed[i].x += di.dx;
            proposed[i].y += di.dy;
            const dj = limitMove(proposed[j], { x: -ux * overlap, y: -uy * overlap });
            proposed[j].x += dj.dx;
            proposed[j].y += dj.dy;
          }
        }
      for (let k = 0; k < points.length; k++) {
        points[k].x = proposed[k].x;
        points[k].y = proposed[k].y;
      }
    }
    for (let i = 0; i < points.length; i++) {
      if (regionCode(points[i], lines) !== codes0[i]) return { ok: false };
      for (const l of lines) {
        if (distPointLine(points[i], l) < lineMargin - 0.5) return { ok: false };
      }
    }
    return { ok: true };
  }

  // Drawing
  const dpr = () => Math.max(1, Math.min(2, window.devicePixelRatio || 1));

  function currentScale() {
    if (!canvasRef.current) return 1;
    const viewBox = viewBoxRef.current;
    const viewW = canvasRef.current.clientWidth;
    const viewH = canvasRef.current.clientHeight;
    const base = Math.min(viewW / viewBox.w, viewH / viewBox.h);
    if (config.zoom === 'fit') return base;
    const factor = parseFloat(config.zoom) || 1;
    return base * factor;
  }

  function canvasToWorld(pt) {
    if (!canvasRef.current) return pt;
    const viewBox = viewBoxRef.current;
    const s = currentScale();
    const viewW = canvasRef.current.clientWidth;
    const viewH = canvasRef.current.clientHeight;
    const tx = (viewW - viewBox.w * s) / 2;
    const ty = (viewH - viewBox.h * s) / 2;
    return {
      x: viewBox.x + (pt.x - tx) / s,
      y: viewBox.y + (pt.y - ty) / s
    };
  }

  function drawScene(ctx, points, weights, lines = null) {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const viewBox = viewBoxRef.current;
    const ratio = dpr();
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    const viewW = canvas.clientWidth;
    const viewH = canvas.clientHeight;
    ctx.clearRect(0, 0, viewW, viewH);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, viewW, viewH);

    const s = currentScale();
    const tx = (viewW - viewBox.w * s) / 2;
    const ty = (viewH - viewBox.h * s) / 2;
    ctx.save();
    ctx.translate(tx, ty);
    ctx.scale(s, s);
    ctx.translate(-viewBox.x, -viewBox.y);

    if (lines) {
      ctx.save();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#000';
      for (const l of lines) {
        const seg = clipLineToRect(l, CANVAS_W, CANVAS_H);
        if (!seg) continue;
        ctx.beginPath();
        ctx.moveTo(seg[0].x, seg[0].y);
        ctx.lineTo(seg[1].x, seg[1].y);
        ctx.stroke();
      }
      ctx.restore();
    }

    ctx.save();
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      ctx.beginPath();
      ctx.arc(p.x, p.y, config.pointSize, 0, Math.PI * 2);
      ctx.fillStyle = '#000';
      ctx.fill();
      const label = String(weights[i]);
      ctx.font = `${config.fontSize}px ui-monospace, Menlo, Consolas, monospace`;
      ctx.lineWidth = Math.max(2, config.fontSize * 0.12);
      ctx.strokeStyle = '#fff';
      ctx.strokeText(label, p.x + (config.pointSize + 4), p.y - (config.pointSize + 2));
      ctx.fillStyle = '#000';
      ctx.fillText(label, p.x + (config.pointSize + 4), p.y - (config.pointSize + 2));
    }
    ctx.restore();

    ctx.restore();

    const sel = selRef.current;
    if (sel.active && !sel.ctrlDrag) {
      ctx.save();
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const r = normalizedRect(sel.startCanvas, sel.currentCanvas);
      ctx.strokeStyle = 'rgba(0,0,0,.8)';
      ctx.fillStyle = 'rgba(0,0,0,.08)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      ctx.fillRect(r.x, r.y, r.w, r.h);
      ctx.restore();
    }
  }

  function normalizedRect(a, b) {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const w = Math.abs(a.x - b.x);
    const h = Math.abs(a.y - b.y);
    return { x, y, w, h };
  }

  function drawCurrent() {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ratio = dpr();
    const cssW = canvas.clientWidth;
    const size = Math.max(200, Math.floor(cssW));
    canvas.style.height = size + 'px';
    const needW = Math.round(size * ratio);
    const needH = Math.round(size * ratio);
    if (canvas.width !== needW || canvas.height !== needH) {
      canvas.width = needW;
      canvas.height = needH;
    }

    const ctx = canvas.getContext('2d');
    if (!puzzle) {
      drawScene(ctx, [], []);
      return;
    }
    drawScene(ctx, puzzle.points, puzzle.weights, showSolution ? puzzle.lines : null);
  }

  useEffect(() => {
    drawCurrent();
  }, [puzzle, showSolution, config]);

  useEffect(() => {
    const handleResize = () => drawCurrent();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [puzzle, showSolution, config]);

  // Main generation function
  const runGeneration = async () => {
    setRunning(true);
    setStatus({ text: 'validating…', type: 'warn' });
    setPuzzle(null);
    setShowSolution(false);
    setLog('');
    viewBoxRef.current = { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H };

    const N = config.n;
    const L = config.lines;
    const P = config.parts;
    const WMAX = config.maxW;
    let seed = config.seed;

    const valid = validateInputs(N, L, P);
    if (!valid.ok) {
      setStatus({ text: 'invalid', type: 'err' });
      addLog('Validation error: ' + valid.msg);
      alert(valid.msg);
      setRunning(false);
      return;
    }

    if (!Number.isInteger(WMAX) || WMAX < 1 || WMAX > 99) {
      setStatus({ text: 'invalid', type: 'err' });
      addLog('Validation error: Max number size must be between 1 and 99.');
      alert('Max number size must be between 1 and 99.');
      setRunning(false);
      return;
    }

    setStatus({ text: 'searching…', type: 'warn' });
    const rng = mulberry32(seed >>> 0);

    await new Promise(r => setTimeout(r, 20));

    let found = false;
    let lastPuzzle = null;

    outer: for (let attempt = 1; attempt <= ATTEMPT_CAP; attempt++) {
      addLog(`Attempt ${attempt} — seeding points…`);
      const pts = generatePoints(N, rng);
      if (!pts) {
        addLog('  failed to place points; retry.');
        continue;
      }

      const candidates = buildCandidateLines(pts, L, rng);
      if (!candidates) {
        addLog('  not enough candidate lines; retry.');
        continue;
      }
      const M = candidates.length;
      addLog(`  candidate lines: ${M}, combos: C(${M},${L})=${nCr(M, L)}`);

      const scored = [];
      const minPerRegion = (N >= 2 * P) ? 2 : 1;

      for (const idxs of combinations(M, L)) {
        const linesSet = idxs.map(i => candidates[i]);

        let ok = true;
        for (const l of linesSet) {
          if (!clipLineToRect(l, CANVAS_W, CANVAS_H)) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;

        if (countRegions(linesSet) < P) continue;

        ok = true;
        for (const l of linesSet) {
          for (const p of pts) {
            if (distPointLine(p, l) < 0.7) {
              ok = false;
              break;
            }
          }
          if (!ok) break;
        }
        if (!ok) continue;

        const part = regionsForPoints(pts, linesSet);
        if (!part) continue;
        if (part.codes.length !== P) continue;

        const vis = visibleRegionCodes(linesSet);
        if (vis.size !== P) continue;

        let covers = true;
        for (const code of part.codes) {
          if (!vis.has(code)) {
            covers = false;
            break;
          }
        }
        if (!covers) continue;

        const sizes = part.codes.map(code => part.map.get(code).length);
        const singles = sizes.filter(k => k === 1).length;
        if (minPerRegion === 2 && singles > 0) continue;

        const minSize = Math.min(...sizes);
        const score = singles * 1000 - minSize;
        scored.push({ idxs, score });
        if (scored.length > COMBO_CAP) break;
      }

      if (scored.length === 0) {
        addLog('  no line sets yield exactly P visible parts (with constraints); retrying.');
        continue;
      }

      scored.sort((a, b) => a.score - b.score);
      const eligibleCombos = scored.map(s => s.idxs);

      for (const idxs of eligibleCombos) {
        const linesSet = idxs.map(i => candidates[i]);
        const part = regionsForPoints(pts, linesSet);
        if (!part) continue;

        const sizes = part.codes.map(code => part.map.get(code).length);
        const Smin = Math.max(...sizes.map(k => 1 * k));
        const Smax = Math.min(...sizes.map(k => WMAX * k));
        if (Smin > Smax) continue;

        for (let t = 0; t < WEIGHT_TRIES_PER_COMBO; t++) {
          const Stry = randInt(rng, Smin, Smax);
          const weightsTry = sampleWeightsForPartition(part, N, Stry, rng, WMAX);
          if (!weightsTry) continue;
          const checkS = checkEqualSums(part, weightsTry);
          if (checkS === null) continue;
          const { hits } = countSolutionsAcrossCombos(candidates, eligibleCombos, pts, weightsTry, P);
          if (hits === 1) {
            lastPuzzle = {
              N, L, P, seed, maxW: WMAX,
              points: pts,
              weights: weightsTry,
              lines: linesSet,
              commonSum: checkS,
              candidateCount: candidates.length,
              checkedCombos: eligibleCombos.length
            };
            found = true;
            addLog(`✅ Unique solution found on attempt ${attempt}.`);
            break;
          }
        }
        if (found) break;
      }
      if (found) break outer;
      if (attempt % 5 === 0) await new Promise(requestAnimationFrame);
    }

    if (!found) {
      setStatus({ text: 'no solution', type: 'err' });
      alert('No unique puzzle found under current inputs.\nTry adjusting N, L, P, or Seed.');
      setRunning(false);
      return;
    }

    // Compaction
    const originalPoints = lastPuzzle.points.map(p => ({ x: p.x, y: p.y }));
    const lineMargin = Math.max(config.pointSize + 2, 6);
    const minSpacing = Math.max(Math.round(config.pointSize * 3), 18);
    const r1 = compactPointsInPlace(lastPuzzle.points, lastPuzzle.lines, {
      iterations: 10,
      alpha: 0.22,
      lineMargin,
      minDist: minSpacing
    });
    if (!r1.ok) {
      lastPuzzle.points = originalPoints.map(p => ({ x: p.x, y: p.y }));
      const r2 = compactPointsInPlace(lastPuzzle.points, lastPuzzle.lines, {
        iterations: 8,
        alpha: 0.12,
        lineMargin,
        minDist: minSpacing
      });
      if (!r2.ok) lastPuzzle.points = originalPoints;
    }

    viewBoxRef.current = { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H };
    setPuzzle(lastPuzzle);
    setStatus({ text: 'ready', type: 'ok' });
    setRunning(false);
  };

  function addLog(line) {
    setLog(prev => prev + line + '\n');
  }

  const handleExport = (includeSolution) => {
    if (!puzzle) return;

    const cfg = EXPORT_CONFIG;
    const SIZE = cfg.EXPORT_SIZE;
    const BORDER = cfg.EXPORT_BORDER_PX;
    const DOT_R_PX = cfg.EXPORT_POINT_R_PX;
    const FONT_PX = cfg.EXPORT_FONT_PX;
    const LINE_PX = cfg.EXPORT_SOLUTION_LINE_PX;

    const out = document.createElement('canvas');
    out.width = SIZE;
    out.height = SIZE;
    const g = out.getContext('2d');

    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, SIZE, SIZE);

    const viewBox = viewBoxRef.current;
    const s = Math.min(SIZE / viewBox.w, SIZE / viewBox.h);
    const tx = (SIZE - viewBox.w * s) / 2;
    const ty = (SIZE - viewBox.h * s) / 2;

    const worldDotR = DOT_R_PX / s;
    const worldFontPx = FONT_PX / s;
    const worldLinePx = LINE_PX / s;

    g.save();
    g.translate(tx, ty);
    g.scale(s, s);
    g.translate(-viewBox.x, -viewBox.y);

    if (includeSolution) {
      g.save();
      g.lineWidth = worldLinePx;
      g.strokeStyle = '#000';
      for (const l of puzzle.lines) {
        const seg = clipLineToRect(l, CANVAS_W, CANVAS_H);
        if (!seg) continue;
        g.beginPath();
        g.moveTo(seg[0].x, seg[0].y);
        g.lineTo(seg[1].x, seg[1].y);
        g.stroke();
      }
      g.restore();
    }

    for (let i = 0; i < puzzle.points.length; i++) {
      const p = puzzle.points[i];
      g.beginPath();
      g.arc(p.x, p.y, worldDotR, 0, Math.PI * 2);
      g.fillStyle = '#000';
      g.fill();
      const label = String(puzzle.weights[i]);
      g.font = `${worldFontPx}px ui-monospace, Menlo, Consolas, monospace`;
      g.lineWidth = Math.max(worldFontPx * 0.12, 1 / s);
      g.strokeStyle = '#fff';
      g.strokeText(label, p.x + (worldDotR + 4 / s), p.y - (worldDotR + 2 / s));
      g.fillStyle = '#000';
      g.fillText(label, p.x + (worldDotR + 4 / s), p.y - (worldDotR + 2 / s));
    }

    g.restore();

    g.save();
    g.lineWidth = BORDER;
    g.strokeStyle = '#000';
    const inset = BORDER / 2;
    g.strokeRect(inset, inset, SIZE - BORDER, SIZE - BORDER);
    g.restore();

    const meta = `seed${puzzle.seed}_N${puzzle.N}_L${puzzle.L}_P${puzzle.P}`;
    const filename = includeSolution ? `puzzle_solution_${meta}.png` : `puzzle_task_${meta}.png`;
    out.toBlob(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    }, 'image/png');
  };

  // Mouse handling
  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    if (e.ctrlKey && puzzle) {
      const idx = hitTestPoint(pt);
      if (idx >= 0) {
        selRef.current = { active: true, ctrlDrag: true, dragIndex: idx, startCanvas: pt, currentCanvas: pt };
        canvasRef.current.style.cursor = 'grabbing';
      }
    } else {
      selRef.current = { active: true, ctrlDrag: false, startCanvas: pt, currentCanvas: pt, dragIndex: -1 };
      canvasRef.current.style.cursor = 'crosshair';
    }
  };

  const handleMouseMove = (e) => {
    if (!selRef.current.active || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    selRef.current.currentCanvas = pt;

    if (selRef.current.ctrlDrag && selRef.current.dragIndex >= 0 && puzzle) {
      const wpt = canvasToWorld(pt);
      wpt.x = Math.max(PAD, Math.min(CANVAS_W - PAD, wpt.x));
      wpt.y = Math.max(PAD, Math.min(CANVAS_H - PAD, wpt.y));
      const newPuzzle = { ...puzzle };
      newPuzzle.points = [...puzzle.points];
      newPuzzle.points[selRef.current.dragIndex] = { ...wpt };
      setPuzzle(newPuzzle);
    }
    drawCurrent();
  };

  const handleMouseUp = () => {
    if (!selRef.current.active || !canvasRef.current) return;

    if (selRef.current.ctrlDrag) {
      selRef.current = { active: false, ctrlDrag: false, dragIndex: -1, startCanvas: null, currentCanvas: null };
      canvasRef.current.style.cursor = 'crosshair';
      drawCurrent();
      return;
    }

    const r = normalizedRect(selRef.current.startCanvas, selRef.current.currentCanvas);
    selRef.current = { active: false, ctrlDrag: false, dragIndex: -1, startCanvas: null, currentCanvas: null };
    canvasRef.current.style.cursor = 'crosshair';

    if (r.w < 8 || r.h < 8) {
      drawCurrent();
      return;
    }

    const w0 = canvasToWorld({ x: r.x, y: r.y });
    const w1 = canvasToWorld({ x: r.x + r.w, y: r.y + r.h });
    const newVB = {
      x: Math.max(0, Math.min(CANVAS_W, Math.min(w0.x, w1.x))),
      y: Math.max(0, Math.min(CANVAS_H, Math.min(w0.y, w1.y)))
    };
    newVB.w = Math.min(CANVAS_W - newVB.x, Math.abs(w1.x - w0.x));
    newVB.h = Math.min(CANVAS_H - newVB.y, Math.abs(w1.y - w0.y));
    if (newVB.w < 60 || newVB.h < 60) {
      drawCurrent();
      return;
    }
    viewBoxRef.current = newVB;
    drawCurrent();
  };

  function hitTestPoint(canvasPt) {
    if (!puzzle || !canvasRef.current) return -1;
    const s = currentScale();
    const viewBox = viewBoxRef.current;
    const viewW = canvasRef.current.clientWidth;
    const viewH = canvasRef.current.clientHeight;
    const tx = (viewW - viewBox.w * s) / 2;
    const ty = (viewH - viewBox.h * s) / 2;
    const world = {
      x: viewBox.x + (canvasPt.x - tx) / s,
      y: viewBox.y + (canvasPt.y - ty) / s
    };
    const idx = puzzle.points.findIndex(p => Math.hypot(p.x - world.x, p.y - world.y) <= config.pointSize * 1.6);
    return idx;
  }

  return (
    <div className="dot-split-generator2">
      <div className="header-card">
        <div className="inputs">
          <div className="field xs">
            <label>Points (N)</label>
            <input
              type="number"
              value={config.n}
              onChange={(e) => setConfig({ ...config, n: parseInt(e.target.value) })}
              min="2"
              max="80"
            />
          </div>
          <div className="field xs">
            <label>Seed</label>
            <input
              type="number"
              value={config.seed}
              onChange={(e) => setConfig({ ...config, seed: parseInt(e.target.value) })}
            />
          </div>
          <div className="field xs">
            <label>Lines (L)</label>
            <input
              type="number"
              value={config.lines}
              onChange={(e) => setConfig({ ...config, lines: parseInt(e.target.value) })}
              min="1"
              max="6"
            />
          </div>
          <div className="field xs">
            <label>Parts (P)</label>
            <input
              type="number"
              value={config.parts}
              onChange={(e) => setConfig({ ...config, parts: parseInt(e.target.value) })}
              min="2"
              max="32"
            />
          </div>
          <div className="field xs">
            <label>Max number size</label>
            <input
              type="number"
              value={config.maxW}
              onChange={(e) => setConfig({ ...config, maxW: parseInt(e.target.value) })}
              min="2"
              max="99"
            />
          </div>
          <div className="field xs">
            <label>Zoom</label>
            <select value={config.zoom} onChange={(e) => setConfig({ ...config, zoom: e.target.value })}>
              <option value="fit">Fit</option>
              <option value="0.75">75%</option>
              <option value="1">100%</option>
              <option value="1.5">150%</option>
              <option value="2">200%</option>
            </select>
          </div>
          <div className="field sm">
            <label>Point size (view) <span className="muted">({config.pointSize})</span></label>
            <input
              type="range"
              value={config.pointSize}
              onChange={(e) => setConfig({ ...config, pointSize: parseInt(e.target.value) })}
              min="4"
              max="24"
            />
          </div>
          <div className="field sm">
            <label>Font size (view) <span className="muted">({config.fontSize})</span></label>
            <input
              type="range"
              value={config.fontSize}
              onChange={(e) => setConfig({ ...config, fontSize: parseInt(e.target.value) })}
              min="14"
              max="48"
            />
          </div>
          <div className="field wide">
            <label>&nbsp;</label>
            <div className="buttons">
              <button onClick={runGeneration} disabled={running}>
                {running ? 'Running...' : 'Run'}
              </button>
              <button
                onClick={() => {
                  const s = 1 + Math.floor(Math.random() * 0x7fffffff);
                  setConfig({ ...config, seed: s });
                  setTimeout(runGeneration, 50);
                }}
                className="ghost"
              >
                New seed + rerun
              </button>
              <button
                onClick={() => setShowSolution(!showSolution)}
                className="secondary"
                disabled={!puzzle}
              >
                {showSolution ? 'Hide solution' : 'Show solution'}
              </button>
              <button
                onClick={() => {
                  viewBoxRef.current = { x: 0, y: 0, w: CANVAS_W, h: CANVAS_H };
                  drawCurrent();
                }}
                className="ghost"
              >
                Reset View
              </button>
              <button onClick={() => handleExport(false)} className="ghost" disabled={!puzzle}>
                Export Task (PNG)
              </button>
              <button onClick={() => handleExport(true)} className="ghost" disabled={!puzzle}>
                Export Solution (PNG)
              </button>
              <span className={`badge ${status.type}`}>{status.text}</span>
            </div>
          </div>
        </div>
      </div>

      <main className="row">
        <div className="left">
          <div className="card canvas-card">
            <canvas
              ref={canvasRef}
              width="840"
              height="840"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              style={{ cursor: 'crosshair' }}
            />
          </div>
        </div>
        <div className="right">
          <div className="card">
            <div className="puzzle-header">
              <strong>Puzzle output</strong>
              <span className={`badge ${puzzle ? 'ok' : 'warn'}`}>
                {puzzle ? 'unique ✓' : 'no puzzle yet'}
              </span>
            </div>
            {puzzle && (
              <div className="summary muted">
                Seed={puzzle.seed} • N={puzzle.N} • L={puzzle.L} • P={puzzle.P} • Max={puzzle.maxW} • candidates={puzzle.candidateCount} • combos checked={puzzle.checkedCombos}
              </div>
            )}
            <details>
              <summary>Points & weights (JSON)</summary>
              <div className="json">
                {puzzle && JSON.stringify({
                  seed: puzzle.seed,
                  N: puzzle.N,
                  L: puzzle.L,
                  P: puzzle.P,
                  points: puzzle.points.map(p => ({ x: +p.x.toFixed(3), y: +p.y.toFixed(3) })),
                  maxW: puzzle.maxW,
                  weights: puzzle.weights
                }, null, 2)}
              </div>
            </details>
            <details>
              <summary>Log</summary>
              <div className="log">{log}</div>
            </details>
          </div>
        </div>
      </main>

      <div className="footer">
        <small>
          Drag to box-crop the view. Hold <b>CTRL</b> to drag dots (free-form; lines unchanged).
          Exports are always <b>640×640</b> and use hardcoded export settings.
        </small>
      </div>
    </div>
  );
};

export default DotSplitGenerator2;
